/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/


import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	forwardRef,
	input,
	Output,
	ViewEncapsulation
} from "@angular/core";
import {
	type ControlValueAccessor,
	FormsModule,
	NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { provideNgxMask } from "ngx-mask";

const noop = () => { };

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => ToggleComponent),
	multi: true,
};

@Component({
	selector: "cb-toggle",
	standalone: true,
	imports: [FormsModule, CommonModule],
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR, provideNgxMask()],

	templateUrl: "./toggle.component.html",
	styleUrl: "./toggle.component.scss",
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleComponent implements ControlValueAccessor {
	/**
	 * This is an input property that accepts a string as the size.
	 */
	public readonly size = input<"sm" | "md">("md");

	/**
	 * This is an input property that accepts a string as the label.
	 */
	public readonly label = input<string>();

	/**
	 * This is an input property that accepts a string as the help text.
	 */
	public readonly hint = input<string>();

	/**
	 * This is an input property that determines whether the toggle is disabled or not.
	 */
	public readonly disabled = input(false);

	/**
	 * This is an input property that determines whether the toggle is required or not.
	 */
	public readonly required = input(false);

	/**
	 * Emits the value of the input on change
	 */
	@Output() public change: EventEmitter<boolean> = new EventEmitter<boolean>();

	private innerValue = false;

	/**
	 * Returns the value of the input
	 */
	public get value(): boolean {
		return this.innerValue;
	}

	/**
	 * Sets the value of the input
	 */
	public set value(v: boolean) {
		if (v !== this.innerValue) {
			this.innerValue = v;
			this.onChangeCallback(v);
			this.change.emit(this.value);
		}
	}

	public writeValue(obj: boolean): void {
		if (obj !== this.innerValue) {
			this.innerValue = obj;
		}
	}

	public onTouchedCallback: () => void = noop;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}

	public toggle() {
		if (this.disabled()) return;
		this.value = !this.value;
	}
}
